import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { useDocumentTitle } from "../a11y";
import { api, FieldsError } from "../api";
import { COMPANY_SIZES, COMPANY_MAX, NAME_MAX, signupPayload, validateSignup, type SignupErrors, type SignupFields } from "../signup";
import type { User } from "../types";
import "../styles/brand.css";

const SITE = "https://operate.legal";
const ORDER: (keyof SignupFields)[] = ["name", "company", "companySize", "email", "phone"];

/**
 * The first screen (19 §4.12): a short sign-up, in the Operate Legal look. Anyone can play; no passwords.
 * Errors are written next to each field, and focus moves to the first field that needs attention.
 */
export function Signup({ orgName, code, onSignedUp }: { orgName: string; code: string | null; onSignedUp: (u: User) => void }) {
  const [f, setF] = useState<SignupFields>({ name: "", company: "", companySize: "", email: "", phone: "" });
  const [errors, setErrors] = useState<SignupErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refs = useRef<Partial<Record<keyof SignupFields, HTMLInputElement | HTMLSelectElement | null>>>({});
  useDocumentTitle(`Leadership simulation · ${orgName}`, { full: true });

  const set = (k: keyof SignupFields) => (e: { target: { value: string } }) => {
    setF((x) => ({ ...x, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };
  const focusFirst = (errs: SignupErrors) => {
    const k = ORDER.find((x) => errs[x]);
    if (k) refs.current[k]?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const errs = validateSignup(f);
    setErrors(errs);
    if (Object.keys(errs).length) { focusFirst(errs); return; }
    setBusy(true);
    try {
      const { user } = await api.signup(signupPayload(f, code));
      onSignedUp(user);
    } catch (err) {
      if (err instanceof FieldsError && Object.keys(err.fields).length) {
        const errs2 = err.fields as SignupErrors;
        setErrors(errs2);
        focusFirst(errs2);
      } else {
        setFormError(err instanceof Error ? `We couldn't sign you up: ${err.message}` : "We couldn't sign you up. Please try again.");
      }
      setBusy(false);
    }
  };

  const field = (k: keyof SignupFields, label: string, optional: boolean, input: (p: {
    id: string; ref: (el: HTMLInputElement | HTMLSelectElement | null) => void; "aria-invalid"?: true; "aria-describedby"?: string;
  }) => ReactNode, hint?: string) => {
    const id = `su-${k}`;
    const describedBy = [hint ? `${id}-hint` : null, errors[k] ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
    return (
      <div className="ol-field">
        <label className="ol-label" htmlFor={id}>{label}{optional && <span className="ol-opt"> (optional)</span>}</label>
        {input({ id, ref: (el) => { refs.current[k] = el; }, "aria-invalid": errors[k] ? true : undefined, "aria-describedby": describedBy })}
        {hint && <p className="ol-hint" id={`${id}-hint`}>{hint}</p>}
        {errors[k] && <p className="ol-error" id={`${id}-error`}>{errors[k]}</p>}
      </div>
    );
  };

  return (
    <div className="ol">
      <header className="ol-top">
        <a className="ol-logo" href={SITE}>
          <span className="ol-logo__mark" aria-hidden="true">O</span>
          <span className="ol-logo__word">Operate <em>Legal</em></span>
        </a>
        <a className="ol-back" href={SITE}><span aria-hidden="true">← </span>Back to operate.legal</a>
      </header>

      <main className="ol-main">
        <p className="ol-eyebrow">Free leadership simulation</p>
        <h1 className="ol-h1">Run the firm for eight weeks. <em>See what it needs from you.</em></h1>
        <p className="ol-lead">
          Step into a 47-person law firm as its managing partner, its practice manager or its operations director.
          Run it your way. At the end you'll see what happened, and why.
        </p>

        <form className="ol-card" onSubmit={submit} noValidate aria-labelledby="su-title">
          <h2 id="su-title" className="visually-hidden">Sign up to play</h2>
          {field("name", "Your name", false, (p) => (
            <input {...p} type="text" name="name" autoComplete="name" maxLength={NAME_MAX + 20} required value={f.name} onChange={set("name")} />
          ))}
          {field("company", "Company", false, (p) => (
            <input {...p} type="text" name="company" autoComplete="organization" maxLength={COMPANY_MAX + 20} required value={f.company} onChange={set("company")} />
          ))}
          {field("companySize", "Company size", true, (p) => (
            <select {...p} name="companySize" value={f.companySize} onChange={set("companySize")}>
              <option value="">Choose…</option>
              {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ))}
          {field("email", "Email", true, (p) => (
            <input {...p} type="email" name="email" autoComplete="email" inputMode="email" value={f.email} onChange={set("email")} />
          ), "So we can send you your results, if you ask.")}
          {field("phone", "Phone", true, (p) => (
            <input {...p} type="tel" name="phone" autoComplete="tel" value={f.phone} onChange={set("phone")} />
          ))}

          <div className="ol-alert" role="alert">{formError && <p>{formError}</p>}</div>
          <div className="ol-actions">
            <button type="submit" className="ol-btn" disabled={busy}>
              {busy ? "Starting…" : <>Start playing <span aria-hidden="true">→</span></>}
            </button>
          </div>
          <p className="ol-privacy">
            {orgName} stores these details and your choices in the game to run it and to improve it. We'll only contact you if you ask us to.
          </p>
        </form>
        <p className="ol-meta">No password · Takes about an hour</p>
      </main>

      <footer className="ol-foot">
        <p>© {orgName}</p>
        <ul>
          <li><a href={`${SITE}/privacy`}>Privacy</a></li>
          <li><a href={SITE}>operate.legal</a></li>
        </ul>
      </footer>
    </div>
  );
}
